import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { stationsApi } from '../../lib/stationsApi';
import { insightsApi } from '../../lib/insightsApi';
import { pastillesSignaux } from '../../lib/stationInsights';
import { messageErreur } from '../../lib/erreurs';
import { libelle, nomLisible } from '../../lib/libelles';
import { formatHeure, formatNombre } from '../../lib/formats';
import { useAuth } from '../../context/AuthContext';
import { PageHeader } from '../../components/ui/page-header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { LoadingState } from '../../components/ui/loading-state';
import { EmptyState } from '../../components/ui/empty-state';
import { ClipboardList, Fuel, Receipt, Tags } from 'lucide-react';
import { TankGauge } from '../../components/stations/TankGauge';
import { ActiviteRecente } from '../../components/stations/ActiviteRecente';
import { CELLULES, Panneau, PanneauVide } from '../../components/stations/Panneau';
import {
  StationPicker,
  lireStationCourante,
  retenirStationCourante,
  useStations,
} from '../../components/stations/StationPicker';
import { nomDuPoste } from './ShiftPage';

/**
 * Accueil du pack Stations (lot 030), maquette « Journée du gérant » : une
 * station = sa journée (poste en cours, cuves, la journée d'hier, prix) ;
 * `reseau` = toutes les stations sur une ligne chacune (le tableau réseau
 * complet arrive au lot 034).
 */

const CREDIT_ET_BONS = ['CREDIT', 'VOUCHER', 'FLEET', 'OWN_TRUCK'];
const MOBILE_ET_CARTE = ['ORANGE_MONEY', 'MOOV_MONEY', 'WAVE', 'CARD'];

/** « 2026-10-28 » → « 2026-10-27 ». */
export const veille = (jour) => {
  const [annee, mois, date] = String(jour || '').split('-').map(Number);
  if (!annee || !mois || !date) return null;
  const precedent = new Date(Date.UTC(annee, mois - 1, date) - 86400000);
  return precedent.toISOString().slice(0, 10);
};

/** « mardi 28 octobre » d'une date de journée. */
export const jourLu = (jour) => {
  const [annee, mois, date] = String(jour || '').split('-').map(Number);
  if (!annee || !mois || !date) return '';
  return new Date(annee, mois - 1, date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};

const somme = (parMoyen, codes) => codes.reduce((total, code) => total + Number((parMoyen || {})[code] || 0), 0);

const enMontant = (visible, valeur) =>
  !visible ? 'Masqué' : valeur === null || valeur === undefined ? '—' : formatNombre(valeur, 0);

const signe = (valeur) => {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return '—';
  const texte = formatNombre(Math.abs(nombre), 0);
  return nombre < 0 ? `−${texte}` : texte;
};

const prixDuProduit = (ligne) =>
  (ligne.unit_prices || []).length === 1 ? formatNombre(ligne.unit_prices[0], 0) : (ligne.unit_prices || []).length ? 'plusieurs' : '—';

// --- Une station ----------------------------------------------------------------------------

const JourneeStation = ({ stationId, stations, onStation }) => {
  const navigate = useNavigate();
  const { canSeeMoney, isSuperAdmin, isAdminSociete } = useAuth();
  const estAdministrateur = Boolean(isSuperAdmin || isAdminSociete);
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(false);

  const charger = useCallback(async (id) => {
    if (!id) return;
    setChargement(true);
    try {
      const poste = await stationsApi.shifts.current(id);
      const aujourdHui = poste.business_date;
      const [journee, hier, prix, signaux, activite] = await Promise.all([
        stationsApi.days.get(id, aujourdHui),
        stationsApi.days.get(id, veille(aujourdHui)),
        stationsApi.prices.active(id).catch(() => null),
        // Les signaux sont un plus : leur absence ne bloque jamais l'accueil.
        insightsApi.station(id).catch(() => null),
        // L'activité récente est un plus aussi : sans elle, le bloc dit simplement qu'il n'y a rien.
        stationsApi.activity.recent(id, 8).catch(() => null),
      ]);
      setDonnees({ poste, journee, hier, prix, signaux, activite });
    } catch (probleme) {
      toast.error(messageErreur(probleme, 'Impossible de charger la journée de la station'));
      setDonnees(null);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    setDonnees(null);
    charger(stationId);
  }, [stationId, charger]);

  const ouvert = donnees?.poste?.shift || null;
  const station = donnees?.poste?.station;
  const visible = Boolean(canSeeMoney) && !donnees?.journee?.amounts_hidden;
  const cuves = donnees?.journee?.tanks || [];
  const basses = cuves.filter((cuve) => cuve.below_threshold);
  const hier = donnees?.hier;
  const hierAArreter = hier && hier.status === 'OPEN' && (hier.shifts || []).length > 0;
  const prix = (donnees?.prix?.items || []).filter((ligne) => ligne.in_station || ligne.price);

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Stations"
        title={station ? nomLisible(station.name, station.code) : 'Station'}
        description="La journée de la station : poste en cours, cuves, la journée d’hier et les prix en vigueur."
        meta={donnees?.poste ? `${jourLu(donnees.poste.business_date)}, ${formatHeure(new Date())}` : undefined}
        alertes={[
          ouvert && {
            niveau: 'attention',
            texte: `${nomDuPoste(ouvert.label)} à clôturer`,
            court: `${nomDuPoste(ouvert.label)} à clôturer`,
          },
          ...basses.map((cuve) => ({
            niveau: 'critique',
            texte: `${cuve.name} sous le seuil`,
            court: `${cuve.product_name || cuve.name} sous le seuil`,
          })),
          hierAArreter && { niveau: 'attention', texte: 'La journée d’hier n’est pas arrêtée', court: 'Hier à arrêter' },
          ...pastillesSignaux(donnees?.signaux, {
            action: estAdministrateur ? { label: 'Ouvrir les cuves', to: '/stations/cuves' } : undefined,
          }),
        ].filter(Boolean)}
        actions={
          <>
            {stations.length > 1 && (
              <StationPicker stations={stations} value={stationId} onChange={onStation} className="shrink-0 sm:w-80" />
            )}
            {donnees && (
              <Button type="button" onClick={() => navigate(`/stations/${stationId}/poste`)}>
                {ouvert ? `Clôturer le ${nomDuPoste(ouvert.label).replace(/^Poste /, 'poste ')}` : 'Ouvrir le poste'}
              </Button>
            )}
          </>
        }
      />

      {chargement && !donnees ? (
        <LoadingState shape="readouts" />
      ) : !donnees ? (
        <EmptyState variant="error" title="Journée indisponible" description="La journée de cette station n’a pas pu être chargée." actionLabel="Réessayer" onAction={() => charger(stationId)} />
      ) : (
        <div className="grid items-stretch gap-4 xl:grid-cols-2">
            <Panneau titre="Cuves">
              {cuves.length === 0 ? (
                <PanneauVide icone={Fuel} texte="Aucune cuve" />
              ) : (
                <div className="flex flex-1 flex-wrap items-center justify-center gap-x-10 gap-y-6 px-3 py-4">
                  {cuves.map((cuve) => (
                    <TankGauge
                      key={cuve.id}
                      name={cuve.name}
                      productName={cuve.product_name}
                      volume={cuve.current_volume_liters}
                      capacity={cuve.capacity_liters}
                      threshold={cuve.min_threshold_liters}
                      tone={String(cuve.product_name || '').toLowerCase().startsWith('super') ? 'success' : 'primary'}
                      variant="carte"
                    />
                  ))}
                </div>
              )}
            </Panneau>
            {ouvert ? (
              <Panneau titre={nomDuPoste(ouvert.label)} droite={<Badge variant="warning">Ouvert depuis {formatHeure(ouvert.opened_at)}</Badge>}>
                <table className={`${CELLULES} h-full`}>
                  <tbody>
                    <tr>
                      <td>Pompistes</td>
                      <td className="text-right">
                        {[...new Set((ouvert.assignments || []).map((item) => item.attendant_name).filter(Boolean))].join(', ') || '—'}
                      </td>
                    </tr>
                    <tr>
                      <td>Fond de caisse</td>
                      <td className="text-right tabular-nums">{enMontant(visible, ouvert.opening_cash)}</td>
                    </tr>
                    <tr>
                      <td>Ventes à crédit et bons</td>
                      <td className="text-right tabular-nums">{enMontant(visible, somme(ouvert.non_cash_by_method, CREDIT_ET_BONS))}</td>
                    </tr>
                    <tr>
                      <td>Mobile money et carte</td>
                      <td className="text-right tabular-nums">{enMontant(visible, somme(ouvert.non_cash_by_method, MOBILE_ET_CARTE))}</td>
                    </tr>
                    <tr>
                      <td>Dépenses</td>
                      <td className="text-right tabular-nums">{enMontant(visible, ouvert.cash_expenses)}</td>
                    </tr>
                  </tbody>
                </table>
              </Panneau>
            ) : (
              <Panneau titre="Poste" droite={<Badge variant="muted">Aucun poste ouvert</Badge>}>
                {donnees.poste.last_closed_shift ? (
                  <table className={`${CELLULES} h-full`}>
                    <tbody>
                      <tr>
                        <td>Dernier poste</td>
                        <td className="text-right">{nomDuPoste(donnees.poste.last_closed_shift.label)}</td>
                      </tr>
                      <tr>
                        <td>Clôturé à</td>
                        <td className="text-right">{formatHeure(donnees.poste.last_closed_shift.closed_at)}</td>
                      </tr>
                      <tr>
                        <td>Écart de caisse</td>
                        <td className="text-right tabular-nums">
                          {visible ? signe(donnees.poste.last_closed_shift.cash_variance) : 'Masqué'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <PanneauVide icone={ClipboardList} texte="Aucun poste clôturé" />
                )}
              </Panneau>
            )}

            <Panneau
              titre="Hier"
              droite={hier ? <Badge variant={hier.status === 'OPEN' ? 'warning' : 'success'}>{libelle('station_journee', hier.status)}</Badge> : null}
            >
              {!hier || (hier.products || []).length === 0 ? (
                <PanneauVide icone={Receipt} texte="Aucune vente hier" />
              ) : (
                <table className={`${CELLULES} h-full`}>
                  <thead>
                    <tr>
                      <th className="text-left">Produit</th>
                      <th className="text-right">Litres vendus</th>
                      <th className="text-right">Prix du litre</th>
                      <th className="text-right">Ventes</th>
                      <th className="text-right">Écart cuve</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hier.products.map((ligne) => (
                      <tr key={ligne.product_id}>
                        <td>{ligne.product_name}</td>
                        <td className="text-right tabular-nums">{formatNombre(ligne.liters, 0)}</td>
                        <td className="text-right tabular-nums">{prixDuProduit(ligne)}</td>
                        <td className="text-right tabular-nums">{enMontant(visible, ligne.amount)}</td>
                        <td className="text-right tabular-nums">—</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>Total</td>
                      <td className="text-right tabular-nums">{formatNombre(hier.totals?.liters, 0)}</td>
                      <td />
                      <td className="text-right tabular-nums">{enMontant(visible, hier.totals?.sales_amount)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              )}
            </Panneau>
            <Panneau titre="Prix en vigueur" droite={<span className="text-xs text-muted-foreground">FCFA le litre</span>}>
              {prix.length === 0 ? (
                <PanneauVide icone={Tags} texte="Aucun prix" />
              ) : (
                <table className={`${CELLULES} h-full`}>
                  <thead>
                    <tr>
                      <th className="text-left">Produit</th>
                      <th className="text-right">Prix</th>
                      <th className="text-right">Plafond</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prix.map((ligne) => (
                      <tr key={ligne.product_id}>
                        <td>{ligne.product_name}</td>
                        <td className="text-right tabular-nums">
                          {ligne.price ? formatNombre(ligne.price.unit_price, 0) : <Badge variant="destructive">Sans prix</Badge>}
                        </td>
                        <td className="text-right tabular-nums">{ligne.ceiling ? formatNombre(ligne.ceiling.ceiling_price, 0) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panneau>
            <ActiviteRecente
              className="xl:col-span-2"
              stationId={stationId}
              lignes={(donnees.activite?.items || []).slice(0, 8)}
              montantsVisibles={Boolean(canSeeMoney) && !donnees.activite?.amounts_hidden}
              jour={donnees.poste?.business_date}
              onOuvrir={navigate}
            />
        </div>
      )}
    </div>
  );
};

// --- Réseau ---------------------------------------------------------------------------------

const Reseau = ({ stations }) => {
  const navigate = useNavigate();
  const { canSeeMoney } = useAuth();
  const [lignes, setLignes] = useState(null);
  const actives = useMemo(() => stations.filter((station) => !station.archived_at), [stations]);

  useEffect(() => {
    let actif = true;
    Promise.all(
      actives.map(async (station) => {
        try {
          const poste = await stationsApi.shifts.current(station.id);
          const hier = await stationsApi.days.get(station.id, veille(poste.business_date));
          const journee = await stationsApi.days.get(station.id, poste.business_date);
          return { station, poste, hier, journee };
        } catch {
          return { station, erreur: true };
        }
      }),
    ).then((resultat) => actif && setLignes(resultat));
    return () => {
      actif = false;
    };
  }, [actives]);

  const ouvrir = (station) => {
    retenirStationCourante(station.id);
    navigate('/stations');
  };

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Stations"
        title="Réseau"
        description="Chaque station sur une ligne : poste en cours, ventes et écart de caisse d’hier, journée arrêtée, cuves sous le seuil."
        meta={`${actives.length} station${actives.length > 1 ? 's' : ''}`}
      />
      {!lignes ? (
        <LoadingState shape="table" />
      ) : (
        <Panneau titre="Hier">
          <table className={CELLULES}>
            <thead>
              <tr>
                <th className="text-left">Station</th>
                <th className="text-left">Poste en cours</th>
                <th className="text-right">Litres</th>
                <th className="text-right">Ventes</th>
                <th className="text-right">Écart de caisse</th>
                <th className="text-left">Journée d’hier</th>
                <th className="text-left">Cuves</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((ligne) => {
                const visible = Boolean(canSeeMoney) && !ligne.hier?.amounts_hidden;
                const basses = (ligne.journee?.tanks || []).filter((cuve) => cuve.below_threshold);
                return (
                  <tr key={ligne.station.id} className="cursor-pointer hover:bg-muted/40" onClick={() => ouvrir(ligne.station)}>
                    <td className="font-medium">{nomLisible(ligne.station.name, ligne.station.code)}</td>
                    {ligne.erreur ? (
                      <td colSpan={6} className="text-muted-foreground">
                        Indisponible
                      </td>
                    ) : (
                      <>
                        <td>{ligne.poste.shift ? nomDuPoste(ligne.poste.shift.label) : '—'}</td>
                        <td className="text-right tabular-nums">{formatNombre(ligne.hier.totals?.liters, 0)}</td>
                        <td className="text-right tabular-nums">{enMontant(visible, ligne.hier.totals?.sales_amount)}</td>
                        <td className="text-right tabular-nums">{visible ? signe(ligne.hier.totals?.cash_variance) : 'Masqué'}</td>
                        <td>
                          <Badge variant={ligne.hier.status === 'OPEN' && (ligne.hier.shifts || []).length ? 'warning' : 'success'}>
                            {ligne.hier.status === 'OPEN' && (ligne.hier.shifts || []).length ? 'À arrêter' : libelle('station_journee', ligne.hier.status)}
                          </Badge>
                        </td>
                        <td>
                          {basses.length ? (
                            <Badge variant="destructive">{basses.map((cuve) => cuve.product_name || cuve.name).join(', ')} sous le seuil</Badge>
                          ) : (
                            '—'
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panneau>
      )}
    </div>
  );
};

// --- Page -----------------------------------------------------------------------------------

const StationsHomePage = ({ reseau = false }) => {
  const navigate = useNavigate();
  const { stations, loading } = useStations();
  const [stationId, setStationId] = useState('');
  const actives = useMemo(() => stations.filter((station) => !station.archived_at), [stations]);

  // La station retenue sur cet appareil, sinon la première.
  useEffect(() => {
    if (!actives.length || actives.some((station) => station.id === stationId)) return;
    const retenue = lireStationCourante();
    setStationId(actives.some((station) => station.id === retenue) ? retenue : actives[0].id);
  }, [actives, stationId]);

  const choisir = useCallback((id) => {
    retenirStationCourante(id);
    setStationId(id);
  }, []);

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      {loading ? (
        <LoadingState shape="readouts" />
      ) : actives.length === 0 ? (
        <EmptyState
          variant="empty"
          title="Aucune station"
          description="Créez la station, ses cuves et ses pistolets dans les réglages."
          actionLabel="Réglages des stations"
          onAction={() => navigate('/stations/reglages')}
        />
      ) : reseau ? (
        <Reseau stations={actives} />
      ) : stationId ? (
        <JourneeStation stationId={stationId} stations={actives} onStation={choisir} />
      ) : (
        <LoadingState shape="readouts" />
      )}
    </div>
  );
};

export default StationsHomePage;
